import { compact } from 'lodash';
import React, { useRef, useState } from 'react';
import { MyAccount } from '../../../ApexOPL/MyAccount/MyAccount';
import { TransferMoney } from '../../../ApexOPL/TransferMoney/TransferMoney';
import { IconButton } from '../../../CommonComponents/OPL/IconButton/IconButton';
import { ListItem } from '../../../CommonComponents/OPL/List/ListItem';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../../../CommonComponents/OPL/Menu/Menu';
import { MarginProps } from '../../../theme/styledProps';
import { useRouteParams } from '../../hooks/useRouteParams';
import { TabSlug } from '../../types';

interface Props extends MarginProps {
  clientId: string;
  accountId: string;
  accountName: string;
  managedAccountId: string;
  currentClient: {
    HasOrionRiskAlyseIntegration: boolean;
    HasRMD: boolean;
    Id: number;
  };
  groupId: string;
  onAccountSave(): void;
  accountDetails: {
    IsClosed: boolean;
  };
}

export const AccountMenu: React.FC<Props> = ({
  clientId,
  accountId,
  accountName,
  managedAccountId,
  currentClient = { hasOrionRiskAlyseIntegration: false },
  groupId,
  onAccountSave,
  accountDetails,
  ...rest
}) => {
  const [isTransferMoneyModalOpen, setIsTransferMoneyModalOpen] = useState(false);

  const [isMyAccountModalOpen, setIsMyAccountModalOpen] = useState(false);

  const triggerRef = useRef<HTMLButtonElement>(null);

  const { tabSlug } = useRouteParams();

  const {
    HasOrionRiskAlyseIntegration: hasRiskalyse,
    HasRMD: hasRMD,
    Id: currentClientId,
  } = currentClient;

  const showAccountItems =
    managedAccountId && accountId && clientId && accountDetails && !accountDetails.IsClosed;

  const handleMyAccountClose = () => {
    setIsMyAccountModalOpen(false);

    if (triggerRef.current) {
      triggerRef.current.focus();
    }
  };

  const menuItems = compact([
    showAccountItems && {
      label: 'Transfer Money',
      icon: 'far fa-exchange',
      onSelect: () => setIsTransferMoneyModalOpen(true),
    },

    showAccountItems && {
      label: 'My Account',
      icon: 'far fa-briefcase',
      onSelect: () => setIsMyAccountModalOpen(true),
    },

    hasRMD && tabSlug !== TabSlug.RmdDashboard
      ? {
          label: 'RMD Summary',
          onSelect() {
            ADV.util.Router.go2('AccountPerformanceReporting', {
              clientId: currentClientId,
              accountId,
              groupId,
              tabSlug: TabSlug.RmdDashboard,
              isApex: false,
            });
          },
        }
      : null,

    hasRiskalyse && !groupId && !accountId && tabSlug !== TabSlug.RiskProfile
      ? {
          label: 'Risk Profile',
          onSelect() {
            ADV.util.Router.go2('AccountPerformanceReporting', {
              clientId: currentClientId,
              accountId,
              tabSlug: TabSlug.RiskProfile,
              isApex: false,
            });
          },
        }
      : null,
  ]);

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <>
      <Menu>
        <MenuTrigger>
          <IconButton
            ref={triggerRef}
            icon="fas fa-ellipsis-h"
            variant="secondary"
            aria-label="Account menu"
            {...rest}
          />
        </MenuTrigger>

        <MenuContent>
          {menuItems.map(({ label, icon, onSelect }) => (
            <MenuItem
              key={label}
              onSelect={e => {
                e.preventDefault();
                onSelect();
              }}
            >
              <ListItem icon={icon}>{label}</ListItem>
            </MenuItem>
          ))}
        </MenuContent>
      </Menu>

      {isTransferMoneyModalOpen && (
        <TransferMoney
          clientId={clientId}
          accountId={accountId}
          accountName={accountName}
          managedAccountId={managedAccountId}
          onClose={() => {
            setIsTransferMoneyModalOpen(false);

            if (triggerRef.current) {
              triggerRef.current.focus();
            }
          }}
        />
      )}

      {isMyAccountModalOpen && (
        <MyAccount
          clientId={clientId}
          accountId={accountId}
          managedAccountId={managedAccountId}
          onSuccess={() => {
            onAccountSave();
            handleMyAccountClose();
          }}
          onClose={handleMyAccountClose}
        />
      )}
    </>
  );
};
